import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { fetchTraders, traderName } from '../../lib/consoleTraders';

// Every trader, searchable, newest activity first. Replaces /admin/traders
// (owner, 2026-10-11: the new console never sends you to the old one).

const SORTS = [
  { k: 'activity', ar: 'آخر نشاط', en: 'Last activity' },
  { k: 'joined', ar: 'الأحدث تسجيلًا', en: 'Newest' },
  { k: 'requests', ar: 'الأكثر طلبات', en: 'Most requests' },
];

const fmt = (iso, isAr) => (iso ? new Date(iso).toLocaleDateString(isAr ? 'ar-SA' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export default function ConsoleTraders({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('activity');
  const [onlyOpen, setOnlyOpen] = useState(false);

  useEffect(() => {
    fetchTraders().then(setRows).catch((e) => { setRows([]); setError(e.message); });
  }, []);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = (rows || []).filter((r) => (!onlyOpen || r.openAlerts > 0)
      && (!s || [r.full_name, r.company_name, r.email, r.phone, r.whatsapp, r.city]
        .some((v) => String(v || '').toLowerCase().includes(s))));
    const key = { activity: (r) => r.lastActivity || '', joined: (r) => r.created_at || '', requests: (r) => r.requestCount };
    return [...list].sort((a, b) => {
      const x = key[sort](a); const y = key[sort](b);
      return x < y ? 1 : x > y ? -1 : 0;
    });
  }, [rows, q, sort, onlyOpen]);

  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="traders">
      <div className="ac-page" style={{ fontFamily: font }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{isAr ? 'التجار' : 'Traders'}</h1>
            <p className="ac-sub">{rows == null ? '…' : (isAr ? `${rows.length} تاجر مسجّل` : `${rows.length} registered traders`)}</p>
          </div>
        </div>

        <div className="ac-chiprow" style={{ marginBottom: 12 }}>
          <input className="ac-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={isAr ? 'ابحث بالاسم أو الشركة أو الجوال أو الإيميل…' : 'Search name, company, phone or email…'}
            style={{ maxWidth: 320, height: 32, fontFamily: font }} />
          {SORTS.map((s) => (
            <button key={s.k} className={`ac-chip${sort === s.k ? ' on' : ''}`} onClick={() => setSort(s.k)}>{isAr ? s.ar : s.en}</button>
          ))}
          <button className={`ac-chip${onlyOpen ? ' on' : ''}`} onClick={() => setOnlyOpen((o) => !o)}>
            {isAr ? 'عليهم تنبيهات مفتوحة' : 'With open alerts'}
          </button>
        </div>

        {error && <div className="ac-placeholder" style={{ color: 'var(--ac-danger)', marginBottom: 12 }}>{error}</div>}

        {rows == null ? <div className="ac-skel" style={{ height: 120 }} /> : shown.length === 0 ? (
          <div className="ac-placeholder">{isAr ? 'لا يوجد تجار.' : 'No traders.'}</div>
        ) : (
          <div className="ac-tablewrap">
            <table className="ac-table">
              <thead>
                <tr>
                  <th>{isAr ? 'التاجر' : 'Trader'}</th>
                  <th>{isAr ? 'التواصل' : 'Contact'}</th>
                  <th>{isAr ? 'المدينة' : 'City'}</th>
                  <th>{isAr ? 'الطلبات' : 'Requests'}</th>
                  <th>{isAr ? 'تنبيهات مفتوحة' : 'Open alerts'}</th>
                  <th>{isAr ? 'آخر نشاط' : 'Last activity'}</th>
                  <th>{isAr ? 'التسجيل' : 'Joined'}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} onClick={() => nav(`/admin2/traders/${r.id}`)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div style={{ fontWeight: 650, color: 'var(--ac-ink)' }}>{traderName(r)}</div>
                      {r.company_name && r.full_name && <div style={{ fontSize: 12, color: 'var(--ac-faint)' }}>{r.full_name}</div>}
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      <div dir="ltr" style={{ textAlign: isAr ? 'right' : 'left' }}>{r.whatsapp || r.phone || ''}</div>
                      <div dir="ltr" style={{ color: 'var(--ac-faint)', textAlign: isAr ? 'right' : 'left' }}>{r.email || ''}</div>
                    </td>
                    <td>{r.city || '—'}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{r.requestCount}</td>
                    <td>{r.openAlerts > 0 ? <span className="ac-badge warn">{r.openAlerts}</span> : <span style={{ color: 'var(--ac-faint)' }}>—</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmt(r.lastActivity, isAr)}</td>
                    <td style={{ whiteSpace: 'nowrap', color: 'var(--ac-faint)' }}>{fmt(r.created_at, isAr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ConsoleShell>
  );
}
